import React, { memo } from 'react';
import styled from 'styled-components';
import { useLanguage } from '../../context/LanguageContext';
import { useScrollToSection } from '../../hooks/useScrollToSection';

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background: transparent;
  overflow: hidden;
`;

const HeroBackgroundContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
`;

const HeroBackgroundImage = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-image: url('/images/hero/GX010233_stabilized.mp4_snapshot_00.44.705~2.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  filter: brightness(0.6);
  z-index: 0;
  pointer-events: none;
`;

const HeroVideo = styled.video`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  object-fit: cover;
  filter: brightness(0.6);
  z-index: 1;
  pointer-events: none;
`;


const HeroContent = styled.div<{ $isRTL: boolean }>`
  text-align: center;
  color: white;
  z-index: 2;
  padding: 0 2rem;
  margin-top: 2rem;
  position: relative;
  
  /* Mobile landscape mode - increase top margin to avoid header overlap */
  @media (max-width: 896px) and (orientation: landscape) {
    margin-top: 7rem;
  }
  
  ${props => props.$isRTL && `
    direction: rtl;
  `}
`;

const HeroTitle = styled.div<{ $isRTL: boolean }>`
  font-size: 4.5rem;
  font-weight: bold;
  color: white;
  line-height: 1.2;
  font-family: ${props => props.$isRTL ? '"Heebo", sans-serif' : '"Inter", sans-serif'} !important;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  text-align: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 3.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 3rem;
  }
`;

const HeroSiteNameEnglish = styled.div`
  font-size: 3rem;
  font-weight: bold;
  color: white;
  line-height: 1.2;
  font-family: "Inter", "Heebo", sans-serif !important;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const HeroLogoContainer = styled.div`
  position: absolute;
  top: 2rem;
  right: 6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;

  @media (max-width: 768px) {
    right: 2.5rem;
    top: 0.7rem;
  }

  @media (max-width: 480px) {
    right: 2.5rem;
    top: 0.7rem;
  }
`;

const HeroLogoImage = styled.img`
  width: 130px;
  height: 130px;
  margin: 0;
  object-fit: contain;
  filter: brightness(1.3) 
          contrast(1.4);
  
  @media (max-width: 768px) {
    width: 90px;
    height: 90px;
  }
  
  @media (max-width: 480px) {
    width: 90px;
    height: 90px;
>>>>>>> 20ca582bd79d93e09f5151540a5131c7f3d1441a
  }
`;

const HeroSubtitle = styled.p<{ $isRTL: boolean }>`
  font-size: 2.2rem;
  font-weight: 500;
  margin-bottom: 2rem;
  opacity: 0.9;
  line-height: 1.6;
  color: #FFD700;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  font-family: ${props => props.$isRTL ? '"Heebo", sans-serif' : '"Inter", sans-serif'} !important;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.6rem;
  }
`;

const HeroDescription = styled.p<{ $isRTL: boolean }>`
  font-size: 1.8rem;
  font-weight: 400;
  margin-bottom: 3rem;
  line-height: 1.6;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  font-family: ${props => props.$isRTL ? '"Heebo", sans-serif' : '"Inter", sans-serif'} !important;
  
  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.4rem;
  }
`;

const CTAButton = styled.button<{ $isRTL: boolean }>`
  background: white;
  color: rgb(41 37 36 / 1);
  border: none;
  padding: 1.5rem 3rem;
  font-size: 1.6rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2), 2px 2px 4px rgba(0, 0, 0, 0.5);
  font-family: ${props => props.$isRTL ? '"Heebo", sans-serif' : '"Inter", sans-serif'} !important;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3), 2px 2px 4px rgba(0, 0, 0, 0.5);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    font-size: 1.4rem;
    padding: 1.2rem 2.5rem;
  }

    /* Mobile landscape mode - hide button */
  @media (max-width: 896px) and (orientation: landscape) {
    display: none;
  }

`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  opacity: 0.7;
  animation: bounce 2s infinite;
  z-index: 2;
  cursor: pointer;
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {
      transform: translateX(-50%) translateY(0);
    }
    40% {
      transform: translateX(-50%) translateY(-10px);
    }
    60% {
      transform: translateX(-50%) translateY(-5px);
    }
  }
`;

const Hero: React.FC = () => {
  const { t, language } = useLanguage();
  const { scrollToSection } = useScrollToSection();
  const isRTL = language === 'he';

  const scrollToNext = () => {
    scrollToSection('gallery');
  };

  return (
    <HeroSection id="hero">
      <HeroBackgroundContainer>
        <HeroBackgroundImage />
        <HeroVideo
          autoPlay
          muted
          loop
          playsInline
        >
          <source src="/videos/hero/fields.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </HeroVideo>
      </HeroBackgroundContainer>
      <HeroContent $isRTL={isRTL}>
        <HeroTitle $isRTL={isRTL}>
          {isRTL ? 'אחוזת האלה' : 'Ella Estate'}
        </HeroTitle>
        <HeroSubtitle $isRTL={isRTL}>{t('hero.subtitle')}</HeroSubtitle>
        <HeroDescription $isRTL={isRTL}>{t('hero.description')}</HeroDescription>
        <CTAButton $isRTL={isRTL} onClick={scrollToNext}>
          {t('hero.cta')}
        </CTAButton>
      </HeroContent>
      <ScrollIndicator onClick={scrollToNext}>
        ↓
      </ScrollIndicator>
    </HeroSection>
  );
};

export default memo(Hero);
